import React, { useState, useMemo } from 'react';
import { Search, X, Gift, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { RankingAward } from 'types/ranking';
import { deleteAward } from 'services/ranking.service';
import CreateAwardModal from '../CreateAwardModal';
import './styles.scss';

interface AwardSelectorProps {
  awards: RankingAward[];
  hasAward: boolean;
  showAward: boolean;
  selectedAwardId?: string;
  onHasAwardChange: (hasAward: boolean) => void;
  onShowAwardChange: (showAward: boolean) => void;
  onAwardSelect: (awardId: string | undefined) => void;
  onAwardCreated: (award: RankingAward) => void;
  onAwardDeleted: (awardId: string) => void;
  loading?: boolean;
}

const AwardSelector: React.FC<AwardSelectorProps> = ({
  awards,
  hasAward,
  showAward,
  selectedAwardId,
  onHasAwardChange,
  onShowAwardChange,
  onAwardSelect,
  onAwardCreated,
  onAwardDeleted,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingAwardId, setDeletingAwardId] = useState<string | null>(null);

  // Filtrar prêmios por busca
  const filteredAwards = useMemo(() => {
    if (!searchTerm.trim()) return awards;

    const term = searchTerm.toLowerCase();
    return awards.filter(
      (award) =>
        award.name?.toLowerCase().includes(term) ||
        award.description?.toLowerCase().includes(term)
    );
  }, [awards, searchTerm]);

  // Selecionar um prêmio
  const selectAward = (awardId: string) => {
    onAwardSelect(awardId === selectedAwardId ? undefined : awardId);
  };

  const handleDeleteAward = async (awardId: string, awardName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!window.confirm(`Você realmente deseja deletar o prêmio "${awardName}"?`)) {
      return;
    }

    try {
      setDeletingAwardId(awardId);
      await deleteAward(awardId);
      
      if (selectedAwardId === awardId) {
        onAwardSelect(undefined);
      }
      
      onAwardDeleted(awardId);
    } catch (error: any) {
      alert(`Erro ao deletar prêmio: ${error?.response?.data?.message || error.message}`);
    } finally {
      setDeletingAwardId(null);
    }
  };

  const handleAwardCreated = (newAward: RankingAward) => {
    onAwardCreated(newAward);
    
    if (newAward._id) {
      onAwardSelect(newAward._id);
    }
  };

  return (
    <div className="award-selector">
      {/* Toggle para habilitar prêmio */}
      <div className="award-toggle-section">
        <div className="toggle-wrapper">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={hasAward}
              onChange={(e) => {
                onHasAwardChange(e.target.checked);
                if (!e.target.checked) {
                  onShowAwardChange(false);
                  onAwardSelect(undefined);
                }
              }}
              className="toggle-input"
            />
            <div className="toggle-switch">
              <div className="toggle-slider"></div>
            </div>
            <span className="toggle-text">
              <Gift size={18} />
              Este ranking possui prêmio
            </span>
          </label>
        </div>

        {hasAward && (
          <div className="toggle-wrapper toggle-secondary">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={showAward}
                onChange={(e) => onShowAwardChange(e.target.checked)}
                className="toggle-input"
              />
              <div className="toggle-switch">
                <div className="toggle-slider"></div>
              </div>
              <span className="toggle-text">
                {showAward ? <Eye size={18} /> : <EyeOff size={18} />}
                Exibir prêmio para os jogadores
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Seleção de prêmio */}
      {hasAward && (
        <>
          <div className="selector-header">
            <div className="search-bar">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Buscar prêmio por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  type="button"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <button
              type="button"
              className="btn-create-award"
              onClick={() => setIsModalOpen(true)}
            >
              <Plus size={18} />
              Criar novo prêmio
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Carregando prêmios...</p>
            </div>
          ) : (
            <>
              {selectedAwardId && (
                <div className="selection-summary">
                  <span className="summary-text">
                    Prêmio selecionado: {awards.find(a => a._id === selectedAwardId)?.name}
                  </span>
                </div>
              )}

              <div className="awards-list">
                {filteredAwards.length > 0 ? (
                  filteredAwards.map((award) => {
                    const isSelected = selectedAwardId === award._id;
                    const isDeleting = deletingAwardId === award._id;

                    return (
                      <div
                        key={award._id}
                        className={`award-item ${isSelected ? 'selected' : ''} ${isDeleting ? 'deleting' : ''}`}
                        onClick={() => !isDeleting && award._id && selectAward(award._id)}
                      >
                        <div className="award-radio">
                          <input
                            type="radio"
                            checked={isSelected}
                            onChange={() => {}}
                            className="radio-input"
                            disabled={isDeleting}
                          />
                          <div className="radio-custom">
                            {isSelected && <div className="radio-dot"></div>}
                          </div>
                        </div>

                        <div className="award-info">
                          {award.image && (
                            <div className="award-image">
                              <img src={award.image} alt={award.name} />
                            </div>
                          )}
                          {!award.image && (
                            <div className="award-placeholder">
                              <Gift size={24} />
                            </div>
                          )}
                          <div className="award-details">
                            <h4 className="award-name">{award.name}</h4>
                            {award.description && (
                              <p className="award-description">{award.description}</p>
                            )}
                          </div>
                        </div>

                        {/* Botão de deletar (como no crudPremios.js) */}
                        <button
                          type="button"
                          className="award-delete-btn"
                          onClick={(e) => award._id && handleDeleteAward(award._id, award.name, e)}
                          disabled={isDeleting}
                          title="Deletar prêmio"
                        >
                          {isDeleting ? (
                            <div className="delete-spinner"></div>
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">
                    <Gift size={48} className="empty-icon" />
                    <h3>Nenhum prêmio encontrado</h3>
                    <p>
                      {searchTerm
                        ? 'Tente buscar por outro termo'
                        : 'Nenhum prêmio cadastrado ainda'}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Modal de Criação */}
      <CreateAwardModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAwardCreated={handleAwardCreated}
      />
    </div>
  );
};

export default AwardSelector;
